import dayjs, { type Dayjs, isDayjs } from "dayjs";
import type React from "react";
import {
  LAST_3_CALENDAR_MONTHS,
  LAST_7_DAYS,
  LAST_28_DAYS,
  LAST_CALENDAR_WEEK,
  LAST_CALENDAR_YEAR,
} from "./DateRangePickerPresets";

import clsx from "clsx";
import { useField } from "formik";
import { isNil } from "lodash-es";
import Datepicker from "react-tailwindcss-datepicker";
import type { DateValueType } from "react-tailwindcss-datepicker/dist/types";
import NewLabelWrapper, { type LabelProps } from "../NewLabelWrapper";
import { allInputStyles, inputStyles } from "../styles";

import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import dateRangePickerStyles from "./DateRangePicker.module.css";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface Common {
  minDate?: Dayjs;
  maxDate?: Dayjs;
  clearable?: boolean;
  minDays?: number;
  showPresets?: boolean;
  placeholder?: string;
  timeZone?: string;
  label?: LabelProps;
  useStringValues?: boolean;
}

type PropsTypes = (
  | {
      startDateName?: never; // name of the form field for the startDate
      endDateName: string; // name of the form field for the endDate
      single?: true;
    }
  | {
      startDateName?: string; // name of the form field for the startDate
      endDateName: string; // name of the form field for the endDate
      single?: never;
    }
) &
  Common;

export default function DateRangePicker(props: PropsTypes): React.ReactElement {
  const startDate = useField(props.startDateName || "unused");
  const endDate = useField(props.endDateName);

  const useStartDate = props.startDateName !== undefined;
  const {
    timeZone = "Europe/London",
    showPresets = true,
    single = false,
    useStringValues = false,
  } = props;

  function formatValueForOutput(
    value: Date,
    startOrEnd: "start" | "end",
  ): Dayjs | string {
    const outValue =
      startOrEnd === "start"
        ? dayjs(value).tz(timeZone).startOf("day")
        : dayjs(value).tz(timeZone).endOf("day");
    return useStringValues ? outValue.toISOString() : outValue;
  }

  function formatValueForInput(value?: Dayjs | string): Date | null {
    if (isDayjs(value)) {
      return value.toDate();
    } else {
      return value ? dayjs(value).toDate() : null;
    }
  }
  const handleSelect = async (value: DateValueType): Promise<any> => {
    if (isNil(value)) {
      const ed = endDate[2].setValue(undefined);
      return useStartDate
        ? startDate[2].setValue(undefined).then(() => {
            return ed;
          })
        : ed;
    } else {
      const sd = useStartDate
        ? value.startDate
          ? startDate[2].setValue(
              formatValueForOutput(value.startDate, "start"),
            )
          : startDate[2].setValue(null)
        : Promise.resolve();
      const ed = value.endDate
        ? single
          ? endDate[2].setValue(formatValueForOutput(value.endDate, "start"))
          : endDate[2].setValue(formatValueForOutput(value.endDate, "end"))
        : endDate[2].setValue(null);
      await sd;
      return ed;
    }
  };

  const disabledDates = [
    props.minDate && {
      endDate: props.minDate.toDate(),
      startDate: new Date(-8640000000000000),
    },
    props.maxDate && {
      startDate: props.maxDate.add(1, "second").toDate(),
      endDate: new Date(8640000000000000),
    },
  ].filter((v) => v !== undefined);

  return (
    <NewLabelWrapper {...props.label}>
      <Datepicker
        value={{
          startDate: useStartDate
            ? formatValueForInput(startDate[0].value)
            : formatValueForInput(endDate[0].value),
          endDate: formatValueForInput(endDate[0].value),
        }}
        popupClassName={
          "transition-all ease-out duration-300 absolute z-50 mt-[1px] text-sm lg:text-xs 2xl:text-sm translate-y-4 opacity-0 hidden"
        }
        containerClassName={clsx(
          dateRangePickerStyles.tailwindDatepicker,
          "relative w-full ",
          inputStyles.text,
        )}
        inputClassName={clsx(
          allInputStyles,
          "relative pr-14 w-full " +
            "tracking-wide " +
            "focus:ring-1 focus:border-0 hover:ring-1 hover:border-0 hover:ring-gray-300 " +
            "disabled:cursor-not-allowed focus:ring-gray-300 ",
        )}
        startFrom={
          useStartDate
            ? formatValueForInput(startDate[0].value)
            : formatValueForInput(endDate[0].value)
        }
        primaryColor={"cyan"}
        onChange={handleSelect}
        readOnly={true}
        displayFormat={"DD/MM/YYYY"}
        showShortcuts={showPresets}
        asSingle={single}
        placeholder={props.placeholder}
        configs={{
          shortcuts: showPresets
            ? {
                last7Days: LAST_7_DAYS,
                lastWeek: LAST_CALENDAR_WEEK,
                last28Days: LAST_28_DAYS,
                lastCalendarMonth: LAST_CALENDAR_WEEK,
                last3Months: LAST_3_CALENDAR_MONTHS,
                last12Months: LAST_CALENDAR_YEAR,
              }
            : {},
        }}
        disabledDates={disabledDates}
      />
    </NewLabelWrapper>
  );
}
